import { RliPreHook } from '@rocketlane/rli-internal-sdk';
import { Dictionary } from 'lodash';
import { stringify } from 'query-string';
import { Currency } from 'api/account';
import { CostRate } from 'api/cost-rates';
import { CustomerPortalProjectMapping } from 'api/customer-portal/api';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { ExportMetaData } from 'api/export/types';
import {
  AuditLog,
  ProjectFinancialsAuditLogMeta,
} from 'api/financials-audit-logs/type';
import { HttpClient } from 'api/http-client';
import {
  Company,
  DateType,
  ProjectId,
  Role,
  Team,
  Timestamp,
  User,
} from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { UserPermissionForProject } from 'api/permissions';
import type { PlaceholderMappingResponse } from 'api/project/placeholder-types';
import { RateCard } from 'api/rate-cards';
import { Space } from 'api/space/space';
import { Sprint } from 'api/sprint-planning/types';
import {
  Field,
  FieldAccessMeta,
  FieldAccessMetaInBase3,
  FieldOption,
  FieldOptionBase,
  FieldsWithUniqueValuesForBulkEditor,
  FieldValue,
  FieldValueType,
  LinkedResource,
  StatusFieldOption,
  Task,
} from 'api/tasks';
import type {
  GroupCriteria,
  GroupedFilter,
  QueryCombinerType,
  FieldFilter,
} from 'api/tasks/filter-types';
import type { PaginatedRequestParams, PaginatedResponse } from 'api/types';
import { ProjectVisibility } from 'api/visibility';
import { Color } from 'components/colors';
import { Filter } from 'components/molecules/filters/types';
import { EffortPlanningConfig } from 'components/organisms/effort-planning-modal/types';
import { PhaseAssignee } from 'components/organisms/project-phase/types';
import { MembersResponse } from 'data/members/api';
import { InvitedMembers } from 'data/members/slice';
import { CreateAndInviteProjectMembers } from 'data/projects/actions';
import type { ProjectTemplateApi } from 'data/templates/projects/types';
import { ProjectEvents } from 'events/projects';
import { TranslationKey, Translate } from 'hooks/use-translation';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import { getSimpleSearchFilterModule } from 'utils/simple-search-filter/module';
import { updateProjectsPartners } from 'utils/strategic-partner-utils';
import {
  AddProjectUserLiteProps,
  AddTeamToProjectPayload,
  AddTeamToProjectResponse,
  BulkShiftDatesRequest,
  ProjectBaselineCreateRequest,
  ProjectBaselineGetResponse,
  ProjectBaselineUpdateRequest,
  ProjectBaselineWriteResponse,
  ProjectProgressPlots,
  ProjectUserLiteMembership,
  RemoveProjectUserLiteProps,
  RemoveTeamFromProjectPayload,
  RemoveTeamFromProjectResponse,
  ProjectFinancialsApiData,
  ProjectFinancialsApiQueryParams,
  ProjectFinancialsApiDataWithQuery,
  ProjectFinancialsDetails,
  ProjectFinancialsContractType,
  ShiftTasksPayload,
  DependenciesMap,
  ProjectFinancalsSubscriptionCurrentPeriodData,
  ProjectFinancialsBudgets,
  BudgetMemberExclusionItem,
  ProjectBudgetItemLite,
  ProjectBudgetDeletePayload,
} from './types';

const mapPhasesFieldAccessMetaInBase3 = (
  phases: ProjectPhase[] | undefined,
): ProjectPhase[] | undefined =>
  phases?.map((phase) =>
    phase.fieldsAccessMeta != null
      ? {
          ...phase,
          fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
            phase.fieldsAccessMeta,
          ),
        }
      : phase,
  );

const mapPhaseFieldAccessMetaInBase3 = (phase: ProjectPhase): ProjectPhase =>
  phase.fieldsAccessMeta != null
    ? {
        ...phase,
        fieldsAccessMetaInBase3: fieldAccessMetaToBase3(phase.fieldsAccessMeta),
      }
    : phase;

/** Applies project-level fieldAccessMetaInBase3 and phase-level mapping for projectPhases, currentPhases, metrics.currentPhases. */
const withProjectMetaAndPhaseMeta = (project: Project): Project => {
  const withProjectMeta =
    project.fieldsAccessMeta != null
      ? {
          ...project,
          fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
            project.fieldsAccessMeta,
          ),
        }
      : project;

  return {
    ...withProjectMeta,
    projectPhases:
      mapPhasesFieldAccessMetaInBase3(withProjectMeta.projectPhases) ??
      withProjectMeta.projectPhases,
    currentPhases:
      mapPhasesFieldAccessMetaInBase3(withProjectMeta.currentPhases) ??
      withProjectMeta.currentPhases,
    ...(withProjectMeta.metrics && {
      metrics: {
        ...withProjectMeta.metrics,
        currentPhases:
          mapPhasesFieldAccessMetaInBase3(
            withProjectMeta.metrics.currentPhases,
          ) ?? withProjectMeta.metrics.currentPhases,
      },
    }),
  };
};

export type AsyncProjectCreateKey = ['async-project-create', number, string];

type PaginatedProjectResponse = PaginatedResponse<Project>;

interface PaginatedProjectParams extends PaginatedRequestParams {
  filter: GroupCriteria[];
  filterType: QueryCombinerType;
}

export interface AggregateProgress {
  atRiskCount?: number;
  overDueCount?: number;
  pendingApprovalCount?: number;
  taskCount?: number;
  csatRating?: number;
}

export interface ProjectFieldValue<T> extends Field {
  fieldId: number;
  fieldValue: T;
  formattedFieldValue?: T;
  metaFieldValue?: FieldOption<T>;
}

// Used for status in the Projects-list page
export interface ProgressStatus
  extends Pick<StatusFieldOption, 'color' | 'value' | 'label' | 'group'> {
  fieldId?: number;
  count?: number;
  key: string;
  type?: string;
  labelKey?: TranslationKey;
}

export interface ProjectPhase {
  fields: FieldValue<FieldValueType>[];
  projectPhaseId: number;
  projectPhaseName: string;
  tasks: Task[];
  dueDate: DateType;
  startDate: DateType;
  private: boolean;
  progressStatus: ProgressStatus[];
  project: Project;
  aggregateProgressValues?: AggregateProgress;
  totalTaskCount?: number;
  entityOrder?: string[];
  dependentProjectPhases?: ProjectPhase[];
  phaseDescription?: string;
  assignees?: PhaseAssignee[];
  dependencyType?: string;
  lag?: number | null;
  isDeleted?: boolean;
  projectFinancialsBudgets?: ProjectBudgetItemLite[];
  projectId?: number;
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
}

export interface Sprints {
  sprintName: string;
  sprintId: number;
}

export interface ProjectWithStatus {
  projectId: number;
  projectName: string;
  statusValue: number;
}

export interface ProjectWithPartners {
  projectId: number;
  partners: PartnerCompany[];
}

export interface ProjectWithFinancialContractType {
  projectId: number;
  projectName: string;
  financialContractType?: ProjectFinancialsContractType;
}

export interface RecordDataProjectPhase extends ProjectPhase {
  projectId: number;
  id: number;
}

export enum VisibilityType {
  ALL = 'EVERYONE',
  INVITED = 'MEMBERS',
}

export enum ProjectStatus {
  InProgress = 2,
  Completed,
  Blocked,
  Proposed,
  InPlanning,
  ToBeStaffed,
  Cancelled,
  OnHold,
}

export enum RestrictedType {
  GROUPS = 'GROUP',
  TEAMMEMBERS = 'MEMBERS',
}

export interface MembersMeta {
  existingEmailDomain: 'string';
  projectMembersMeta: {
    taskAssignedCount: number;
    user: {
      userId: number;
      identityType: string;
    };
  }[];
}

export type ProjectSource = {
  sourceType: string;
  sourceId: number;
  metaData: {
    templateName: string;
  };
  templateName: string;
};

export enum InferredProgressLabels {
  RunningLate = 'Running Late',
  OnTrack = 'On Track',
  AHeadOfTime = 'Ahead of time',
  None = 'None',
}

export enum InferredProgressValue {
  RunningLate = 'RUNNING_LATE',
  OnTrack = 'ON_TRACK',
  AHeadOfTime = 'AHEAD_OF_TIME',
  None = 'NONE',
}

export interface InferredProgress {
  color: Color;
  label: InferredProgressLabels;
  value: InferredProgressValue;
}

export const InferredProgressOptions = (
  translate: Translate,
): FieldOptionBase<InferredProgressValue>[] => [
  {
    label: translate('api.status_updates.inferred_progress.running_late'),
    value: InferredProgressValue.RunningLate,
    color: 'red',
    active: true,
  },
  {
    label: translate('api.status_updates.inferred_progress.on_track'),
    value: InferredProgressValue.OnTrack,
    color: 'green',
    active: true,
  },
  {
    label: translate('api.status_updates.inferred_progress.ahead_of_time'),
    value: InferredProgressValue.AHeadOfTime,
    color: 'blue',
    active: true,
  },
  {
    label: translate('api.status_updates.inferred_progress.none'),
    value: InferredProgressValue.None,
    color: 'gray',
    active: true,
  },
];
export interface Project {
  annualizedRecurringRevenue: number;
  dueDate: DateType;
  projectId: number;
  accountId?: number;
  projectName: string;
  projectPhases: ProjectPhase[];
  projectOwner: User;
  customerChampion: User;
  currentPhases?: ProjectPhase[];
  customerPortalId?: number;
  inferredProgress?: InferredProgress;
  inferredProgressDays?: number;
  startDate: DateType;
  allocationStartDate?: DateType;
  allocationDueDate?: DateType;
  status: string; // TODO:: This is a part of fieds now, can we remove this @abhi, @sha
  customer: Company;
  spaceList: Space[];
  revenue?: number;
  aggregateProgressValues?: AggregateProgress;
  fields: ProjectFieldValue<FieldValueType>[];
  visibility: VisibilityType;
  visibilitySettings?: ProjectVisibility;
  source?: ProjectSource;
  progressStatus: ProgressStatus[];
  pastProjectStatus: PastProjectStatus[];
  projectFee: number;
  doesSprintsExist: boolean;
  metrics: {
    progressPercent: number;
    currentPhases: ProjectPhase[];
    totalAllocatedHours?: number;
    actualCost?: number;
    actualProfit?: number;
    actualProfitMargin?: number;
    actualRevenue?: number;
    totalRecognisedRevenue?: number;
    estimatedCost?: number;
    estimatedProfit?: number;
    estimatedProfitMargin?: number;
    estimatedRevenue?: number;
    estimateAtCompletion?: number;
    estimateToComplete?: number;
    trackedHours?: number;
    billableHours?: number;
  };
  teamMembers?: User[];
  /** Only available in list */
  totalMinutes?: number;
  linkedResources?: LinkedResource[];
  /** Timesheet fields */
  budgetedHours?: number;
  budgetedMinutes?: number;
  totalAllocatedHours?: number;
  /* TODO::FIX_ME use it only in project list */
  totalTaskCount?: number;
  createdAt: number;
  autoAllocation?: boolean;
  sourceTemplates?: ProjectSource[];
  template?: number[];
  templates?: number[];
  metaData?: Record<number, unknown>;
  partners: Company[];
  customerMembersAccepted?: number;
  customerMembersInvited?: number;
  currency?: Currency;
  plannedDuration?: number;
  projectAge?: number;
  percentageBudgetedHoursConsumed?: number;
  percentageBudgetConsumed?: number;
  projectFinancialsDetailModel?: ProjectFinancialsDetails;
  financialContractType?: ProjectFinancialsContractType;
  fieldsAccessMeta: FieldAccessMeta;
  fieldsAccessMetaInBase3: FieldAccessMetaInBase3;
  userGroupIds?: number[];
  asyncProjectCreation?: boolean;
  currentPeriodData?: ProjectFinancalsSubscriptionCurrentPeriodData;
  permissionSet: {
    permissionSetId: number;
    permissionSetType: number;
    permissionSetName: string;
  };
  deleted?: boolean;
  archived?: boolean;
  effortPlanning?: EffortPlanningConfig;
  remainingMinutes?: number;
  projectFinancialsBudgetId?: number; // Used for Timesheet week view.
}

export type ProjectForRm = Omit<
  Project,
  'field' | 'fieldsAccessMeta' | 'fieldsAccessMetaInBase3' | 'teamMembers'
>;

export interface PeopleProjectForRm extends ProjectForRm {
  role: Role;
  refTeam: Team;
}

export interface ProjectLightV2Rm {
  data: PeopleProjectForRm[];
  page: number;
  count: number;
}

export interface PeopleProjectLightV2Rm {
  userIds: Record<string, ProjectLightV2Rm>;
  teamIds: Record<string, ProjectLightV2Rm>;
}

export interface ProjectLightV2RmPayload {
  searchValue?: string;
  searchFilter?: GroupedFilter;
  projectId?: number | null;
  page?: number;
  limit: number;
  getRangeProjects: boolean;
  visibleStartDate?: DateType;
  visibleEndDate?: DateType;
}

interface RmEntity {
  id: number;
  scroll?: 0 | 1;
  projectId?: number | null;
  page?: number;
}

interface RmFilterPayload {
  limit: number;
  match?: QueryCombinerType;
  getRangeProjects: 'true' | 'false'; // TODO: @cyril discuss and cleanup contract maybe
  // getRangeAllocations: true; //FIXME: @cyril re-enable once star schema is fixed
  visibleStartDate: DateType;
  visibleEndDate: DateType;
  searchValue?: string;
  additionalFields?: Record<string, string>;
}

export interface PeopleFilterRmPayload {
  type: 'PEOPLE_FILTER';
  filter: Partial<RmFilterPayload>;
}

export interface PeopleProjectRmPayload {
  type: 'PEOPLE_TO_PROJECT' | 'PEOPLE_TO_PROJECT_V0' | 'PEOPLE_FILTER';
  filter: RmFilterPayload;
  users: RmEntity[];
  teams: RmEntity[];
}

export interface ArchivedProject extends Project {
  archivedOn: Timestamp;
  archivedBy: User;
}

export interface CurrentPhasesMap {
  visiblePhase: string;
  visiblePhaseProgressStatus: ProgressStatus[];
  otherPhases: string[];
}

export interface ProjectMeta {
  name: string;
  logo: string;
  id: number;
}

export interface ProjectHours {
  budgetMinutes?: number;
  total_minutes: number;
  project_name: string;
  billableMinutes?: number;
  remainingMinutes?: number;
  nonBillableHoursFlagEnabled?: boolean;
}
export interface ProjectListItem
  extends Omit<Project, 'status' | 'currentPhases' | 'teamMembers'> {
  lastMilestoneCSAT?: number;
  key: string;
  projectMeta: ProjectMeta;
  projectHours: ProjectHours;
  createdAt: number;
  currentPhases?: CurrentPhasesMap;
  progressPercent: number;
  totalTaskCount: number;
  csatRating: number;
  // mapping arrays to hash
  fieldsMap?: {
    // TODO: remove after converting the main object fields
    [key: string]: ProjectFieldValue<FieldValueType>;
  };
  projectPhasesMap?: ProjectPhaseMap[];
  status: StatusFieldOption;
  aggregatePhaseProgressStatus: ProgressStatus[];
  teamMembers?: Pick<User, 'userId'>[];
}

export interface ProjectUpdatePayload extends Omit<Project, 'fields'> {
  fields?: FieldValue<FieldValueType>[];
}

export enum ProjectSourceType {
  Template = 'TEMPLATE',
}

export type ProjectCreateLinkedResource = Pick<
  LinkedResource,
  | 'integrationId'
  | 'externalResourceType'
  | 'externalResourceId'
  | 'externalIntegrationType'
  | 'enabled'
  | 'context'
>;

interface Source {
  sourceId: number;
  sourceType: ProjectSourceType;
}

export interface PlaceholderAssignmentMap {
  teamId: number;
  userId?: number;
  companyId?: number;
  roleId?: number;
}
export interface CreateProjectPayload {
  customer: Pick<Company, 'companyId'>;
  projectName: string;
  dueDate?: DateType;
  startDate?: DateType;
  source?: Source;
  projectOwner: Pick<User, 'userId'>;
  customerChampion?: Pick<User, 'userId'> | null;
  teamMembers?: Pick<User, 'userId'>[];
  visibility: VisibilityType;
  fields?: Array<FieldValue<FieldValueType>>;
  status?: FieldValue<number>;
  annualizedRecurringRevenue?: number;
  projectFee?: number;
  linkedResources?: ProjectCreateLinkedResource[];
  sources?: ProjectBulkMergePayload[];
  budgetedHours?: number;
  budgetedMinutes?: number;
  teamToUserMapRequests?: PlaceholderAssignmentMap[];
  partners: Pick<Company, 'companyId'>[];
  customerPortalId?: number;
  projectFinancialsDetailModel?: Pick<
    ProjectFinancialsDetails,
    | 'financialContractType'
    | 'rateCardId'
    | 'projectBudget'
    | 'projectHours'
    | 'projectMinutes'
    | 'projectFinancialsMetaData'
  >;
  currency?: Currency;
  userGroupIds?: number[];
}

export type UpdateProjectPayload = Partial<CreateProjectPayload> &
  Pick<
    Project,
    'budgetedHours' | 'budgetedMinutes' | 'customerPortalId' | 'effortPlanning'
  > & {
    shiftAllocations?: boolean;
    autoAllocation?: boolean;
  };

export interface PastProjectStatus {
  projectStatusId: number;
  projectStatusName: string;
  updatedAt: number;
  updatedBy: User;
}

export interface ProjectPhaseMap {
  id: number;
  name?: string;
  inProgress: number;
  completed: number;
  total: number;
  startDate: DateType;
  status: StatusFieldOption;
  private?: boolean;
}

export interface ProjectMembers
  extends Omit<Partial<Project>, 'accountId'>,
    MembersResponse {
  accountId?: number | null;
}

export interface CurrentPhase extends ProjectPhase {
  approvalPending: number;
  atRiskCount: number;
  overDueTaskCount: number;
  status: string;
  totalTaskCount: number;
}

export interface ProjectMergePayload {
  source: Pick<ProjectSource, 'sourceType' | 'sourceId'>;
}

export interface ProjectBulkMergePayload {
  source: Source;
  startDate: DateType;
  prefix?: string;
  includeSpaces?: boolean;
  projectFinancialsBudgetId?: number;
}

export interface ProjectToTemplatePayload {
  templateName: string;
  descriptionFlag: boolean;
  attachmentFlag: boolean;
  assignmentsFlag: boolean;
  fieldsFlag: boolean;
  dependenciesFlag: boolean;
}

interface PaginatedParams extends PaginatedRequestParams {
  filter: Filter;
  pageSource?: string;
}

interface PaginatedParamsV2 extends PaginatedRequestParams {
  filter: SimpleSearchFilter;
  pageSource?: string;
}

export type ProjectBulkMergeResult = Pick<
  Project,
  'projectPhases' | 'spaceList' | 'startDate'
> & { source: Source };

interface GptSearchPayload {
  input: string;
}

export enum ProjectCreationStatusState {
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export interface ProjectCreationStatus {
  userId: number;
  requestId: string;
  accountId: number;
  status: ProjectCreationStatusState;
  taskCompleted: number;
  taskOverall: number;
  projectid: number;
}

export interface UpdateAllowedDomainsForProject {
  allowedDomains: string[];
}

export interface GenerateShareUrlForProjectRequest {
  currentPageUrl: string;
}

export interface GenerateShareUrlResponse {
  shareUrl: string;
}

export interface AddUsersFromShareUrlRequest {
  shareToken: string;
  user: Pick<User, 'emailId'>;
}

export interface AddUsersFromShareUrlResponse {
  success: boolean;
}

export enum ProjectAccessRequestStatus {
  Pending = 'PENDING',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
}

export interface ProjectAccessRequest {
  projectId: number;
  userId: number;
  requestStatus: ProjectAccessRequestStatus;
  user: User;
}

export interface ApproveOrDenyProjectAccessPayload {
  userId: number;
}

export type BulkProjectPayload = {
  projects: Array<{ projectId: number } & Partial<Project>>;
};

export class ProjectAPIClient extends HttpClient {
  public async getProjectList(): Promise<Project[]> {
    return this.get<Project[]>('/projects?lite=true');
  }

  public async createProjectToTemplate(
    projectId: number,
    payload: ProjectToTemplatePayload,
  ): Promise<ProjectTemplateApi> {
    return this.post(`/projects/${projectId}/create-template`, payload);
  }

  public async getProjectsLight(): Promise<Project[]> {
    const data = await this.get<Project[]>('/projects/light');
    const projectData = data.map(withProjectMetaAndPhaseMeta);

    return updateProjectsPartners(projectData) as Project[];
  }

  public async getProjectsLightForRM(): Promise<ProjectForRm[]> {
    const projectData = await this.get<ProjectForRm[]>('/projects/lightRM');

    return updateProjectsPartners(projectData);
  }

  public async getPaginatedProjectsForRM(queryParams: string) {
    return this.get<ProjectLightV2Rm>(`/projects/rm-light?${queryParams}`);
  }

  public async getFilteredPeopleForRM(
    payload: PeopleFilterRmPayload,
  ): Promise<{ users: number[] }> {
    return this.post('/projects/get-people-projects', payload);
  }

  public async getPaginatedPeopleProjectsForRM(
    payload: PeopleProjectRmPayload,
  ): Promise<PeopleProjectLightV2Rm> {
    return this.post('/projects/get-people-projects', payload);
  }

  public async getProjectsMeta(): Promise<Project[]> {
    return this.get<Project[]>('/projects/meta');
  }

  public async getArchivedProjectList(): Promise<Project[]> {
    return this.get<Project[]>('/projects/get-archived');
  }

  public async getDeletedProjectList(): Promise<Project[]> {
    return this.get<Project[]>('/projects/get-deleted');
  }

  public async getProjectDetails(
    projectId: number,
    params = {},
  ): Promise<Project> {
    return this.get<Project>(`/projects/${projectId}`, { params }).then(
      withProjectMetaAndPhaseMeta,
    );
  }

  public async getSprints(projectId: number, params = {}): Promise<Sprint[]> {
    return this.get<Sprint[]>(`/projects/${projectId}/sprints`, {
      params,
    });
  }

  public async getSprintsForProject(
    projectId: number,
    params = {},
  ): Promise<Sprint[]> {
    return this.get<Sprint[]>(
      `/sprints/get-sprints-dropdown?projectId=${projectId}`,
      {
        params,
      },
    );
  }

  public getPaginatedProject = async ({
    startRow,
    endRow,
    filter,
    sort,
    groupBy,
    search,
    pageSource,
  }: PaginatedParams): Promise<PaginatedProjectResponse> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    let url = '/projects/lightV1?';
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    const filterAsString = ApiFilter.toQueryStringFormat(filter.criteria);

    if (groupBy) {
      url += `&groupBy=${groupBy}&`;
    }
    if (filter.criteria.length) {
      url += `&${filterAsString}&match=${filter.type}&`;
    }

    if (search) {
      url += `searchValue=${search}&`;
    }

    if (sort) {
      url += `sort=${sort}&`;
    }
    if (pageSource) {
      url += `pageSource=${pageSource}&`;
    }
    url += `offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedProjectResponse>(url).then((response) => ({
      ...response,
      data: response.data.map(withProjectMetaAndPhaseMeta),
    }));
  };

  public postPaginatedProject = async ({
    startRow,
    endRow,
    filter,
    sort,
    groupBy,
    search,
    pageSource,
  }: PaginatedParamsV2): Promise<PaginatedProjectResponse> => {
    let url = '/projects/lightV1?';

    if (groupBy) {
      url += `&groupBy=${groupBy}&`;
    }

    if (search) {
      url += `searchValue=${search}&`;
    }

    if (sort) {
      url += `sort=${sort}&`;
    }
    if (pageSource) {
      url += `pageSource=${pageSource}&`;
    }
    url += `offset=${startRow}&limit=${endRow - startRow}`;

    return this.post<PaginatedProjectResponse, { filter: typeof filter }>(url, {
      filter,
    }).then((response) => ({
      ...response,
      data: response.data.map(withProjectMetaAndPhaseMeta),
    }));
  };

  public getProjectsByCustomer = async (
    companyId: number,
  ): Promise<Project[]> => {
    return this.get<PaginatedProjectResponse>(
      `/projects/lightV1?project*customer.value=${companyId}`,
    ).then((response) => response.data.map(withProjectMetaAndPhaseMeta));
  };

  public getProjectGroupMeta = async ({
    filter,
    groupName,
    search,
  }: {
    groupName: string;
    filter: Filter;
    search?: string;
  }): Promise<Record<string, number>[]> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    let url = `projects/lightV1/group?groupBy=${groupName}`;
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    const filterAsString = ApiFilter.toQueryStringFormat(filter.criteria);

    if (search) {
      url += `&searchValue=${search}`;
    }

    if (filter.criteria.length) {
      url += `&${filterAsString}&match=${filter.type}&`;
    }

    return this.get(url);
  };

  public postProjectGroupMeta = async ({
    filter,
    groupName,
    search,
  }: {
    groupName: string;
    filter: SimpleSearchFilter;
    search?: string;
  }): Promise<Record<string, number>[]> => {
    let url = `projects/lightV1/group?groupBy=${groupName}`;

    if (filter.nativeFields?.length) {
      url += `&match=${filter.match}`;
    }

    if (search) {
      url += `&searchValue=${search}`;
    }

    return this.post<Record<string, number>[], typeof filter>(url, filter);
  };

  public getMembers = async (projectId: number): Promise<ProjectMembers> => {
    if (projectId == null || Number.isNaN(projectId)) {
      return Promise.resolve<ProjectMembers>({
        members: [],
      });
    }

    return this.get<ProjectMembers>(`/projects/${projectId}/members`);
  };

  public async updateProjectPhase(
    projectId: number,
    phaseId: number,
    payload: Partial<ProjectPhase>,
    isSync?: boolean,
  ): Promise<ProjectPhase> {
    return this.put<ProjectPhase, Partial<ProjectPhase>>(
      `/projects/${projectId}/phases/${phaseId}${
        isSync ? `?isSync=${true}` : ''
      }`,
      payload,
    ).then(mapPhaseFieldAccessMetaInBase3);
  }

  public async createProjectPhase(
    projectId: number,
    projectPhase: Partial<ProjectPhase>,
  ): Promise<ProjectPhase> {
    return this.post<ProjectPhase, Partial<ProjectPhase>>(
      `/projects/${projectId}/phases`,
      projectPhase,
    ).then(mapPhaseFieldAccessMetaInBase3);
  }

  public async deleteProjectPhase(
    projectId: number,
    projectPhaseId: number,
  ): Promise<void> {
    return this.delete(`/projects/${projectId}/phases/${projectPhaseId}`);
  }

  public async createProject(payload): Promise<Project> {
    return this.post<Project, any>(
      '/projects?hasfieldsAccessMeta=false',
      payload,
    ).then(withProjectMetaAndPhaseMeta);
  }

  public async createAndInviteProjectMembers(
    projectId: number,
    payload: CreateAndInviteProjectMembers[],
  ): Promise<Project> {
    return this.post(
      `/projects/${projectId}/invite-members?async=true`,
      payload,
    );
  }

  public async getProjectMembers(projectId: number): Promise<ProjectMembers> {
    return this.get(`/projects/${projectId}/get-members`);
  }

  @RliPreHook({
    event: ProjectEvents.ProjectUpdate,
    dataGetter: (projectId, data) => ({
      projectId,
      project: data,
    }),
  })
  public async updateProject(
    projectId: number,
    payload: UpdateProjectPayload,
  ): Promise<Project> {
    return this.put<Project, UpdateProjectPayload>(
      `/projects/${projectId}`,
      payload,
    ).then(withProjectMetaAndPhaseMeta);
  }

  public async exportProjectAuditLogs(
    projectId: number,
  ): Promise<{ downloadUrl: string }> {
    return this.get(`/projects/${projectId}/project-audit-log/export`);
  }

  public async deleteProject(projectId: number): Promise<void> {
    return this.delete(`/projects/${projectId}`);
  }

  public async archiveProject(projectId: number): Promise<void> {
    return this.post(`/projects/${projectId}/archive`);
  }

  public async bulkArchiveProject(payload: number[]): Promise<void> {
    return this.put('bulk/projects/archive', payload);
  }

  public async unarchiveProject(projectId: number): Promise<void> {
    return this.post(`/projects/${projectId}/unarchive`);
  }

  public async bulkUpdateProject(data: BulkProjectPayload) {
    return this.put('/bulk/projects', data);
  }

  public async addMembers(
    projectId: number,
    payload: InvitedMembers[],
  ): Promise<InvitedMembers[]> {
    return this.post(
      `/projects/${projectId}/add-members`,
      payload.map((p) => ({
        ...p,
        permissionSet: undefined,
      })),
    );
  }

  public async updateMembers(
    projectId: number,
    payload: {
      user: {
        userId: number;
      };
      permissionSet?: {
        permissionSetType: number;
        permissionSetId?: number;
      };
    }[],
  ): Promise<any> {
    return this.put(`/projects/${projectId}/update-members`, payload);
  }

  public async removeMembers(
    projectId: number,
    payload: {
      userId: number;
    }[],
  ): Promise<any> {
    return this.delete(`/projects/${projectId}/remove-members`, {
      data: payload,
    });
  }

  public async getProjectMembersMeta(projectId: number): Promise<MembersMeta> {
    return this.get(`/projects/${projectId}/get-meta`);
  }

  public async getProgress(projectId: number): Promise<ProjectProgressPlots> {
    return this.get(`/projects/${projectId}/progress`);
  }

  public async merge(projectId: number, payload): Promise<any> {
    return this.put(`/projects/${projectId}/merge`, payload);
  }

  public async getPreviewAPIKey(projectId: number): Promise<any> {
    return this.get(`/projects/${projectId}/customer-access`);
  }

  public exportAsCSV = async (
    filters: FieldFilter[],
    match: QueryCombinerType,
    columnOrder: string[],
  ): Promise<ExportMetaData> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    // const filterString = ApiFilter.toQueryStringFormat(filters);
    // const filterObject = ApiFilter.convertQueryStringToObject(filterString);

    // const request: ExportRequest = {
    //   match: filters.length ? match : QueryCombinerType.All,
    //   ...filterObject,
    // };
    // const data: ExportPayload = {
    //   statusType: ExportStatusType.ProjectExport,
    //   statusMeta: { request },
    // };
    return this.post<ExportMetaData, { columnOrder: string[] }>(
      `reports/project-list/export?${ApiFilter.toQueryStringFormat(
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        filters,
      )}&${stringify(
        {
          match: filters.length ? match : null,
        },
        { skipNull: true },
      )}`,
      { columnOrder },
    ).then((response) => response);
  };

  public async bulkMergePreview(
    projectId: number,
    payload: ProjectBulkMergePayload[],
  ): Promise<ProjectBulkMergeResult[]> {
    return this.post(`/projects/${projectId}/bulk-merge-preview`, payload);
  }

  public async bulkMerge(
    projectId: number,
    payload: ProjectBulkMergePayload[],
  ): Promise<Project> {
    return this.put(`/projects/${projectId}/bulk-merge`, payload);
  }

  public async addTeams(
    projectId: number,
    payload: AddTeamToProjectPayload[],
  ): Promise<AddTeamToProjectResponse[]> {
    return this.post(`/projects/${projectId}/add-teams`, payload);
  }

  public async removeTeams(
    projectId: number,
    payload: RemoveTeamFromProjectPayload[],
  ): Promise<RemoveTeamFromProjectResponse[]> {
    return this.delete(`/projects/${projectId}/remove-teams`, {
      data: payload,
    });
  }

  public async getAllProjectUsersLite(): Promise<ProjectUserLiteMembership[]> {
    return this.get('/projects/get-user-teams');
  }

  public async getProjectUsersLiteForProject(
    projectId: number,
  ): Promise<ProjectUserLiteMembership> {
    return this.get(`/projects/${projectId}/get-user-teams`);
  }

  public async addProjectUsersLite(
    projectId: number,
    payload: AddProjectUserLiteProps,
  ): Promise<AddProjectUserLiteProps> {
    return this.post(`/projects/${projectId}/add-user-teams`, payload);
  }

  public async removeProjectUsersLite(
    projectId: number,
    payload: RemoveProjectUserLiteProps,
  ): Promise<RemoveProjectUserLiteProps> {
    return this.delete(`/projects/${projectId}/remove-user-teams`, {
      data: payload,
    });
  }

  public getProjectBaselines = (
    projectId: number,
  ): Promise<ProjectBaselineGetResponse> =>
    this.get<ProjectBaselineGetResponse>(`/projects/${projectId}/baseline`);

  public createProjectBaseline = (
    projectId: number,
    payload: ProjectBaselineCreateRequest,
  ): Promise<ProjectBaselineWriteResponse> =>
    this.post(`/projects/${projectId}/baseline`, payload);

  public updateProjectBaseline = (
    projectId: number,
    baselineId: number,
    payload: Pick<ProjectBaselineUpdateRequest, 'baselineName'>,
  ): Promise<ProjectBaselineWriteResponse> =>
    this.put(`/projects/${projectId}/baseline/${baselineId}`, payload);

  public deleteProjectBaseline = (
    projectId: number,
    baselineId: number,
  ): Promise<ProjectBaselineWriteResponse> =>
    this.delete(`/projects/${projectId}/baseline/${baselineId}`);

  public async getPlaceholderResolution([
    ,
    projectId,
  ]: ProjectId): Promise<PlaceholderMappingResponse> {
    return this.get(`/projects/${projectId}/get-team-sources`);
  }

  public async getAllProjectsPlaceholderResolution(): Promise<PlaceholderMappingResponse> {
    return this.get('/projects/get-team-sources');
  }

  public addPartners = (
    projectId: number,
    partnerCompanyIds: number[],
  ): Promise<any> =>
    this.post(
      `/projects/${projectId}/add-partner`,
      partnerCompanyIds.map((id) => ({ companyId: id })),
    );

  public removePartners = (
    projectId: number,
    partnerCompanyIds: number[],
  ): Promise<any> =>
    this.post(
      `/projects/${projectId}/remove-partner`,
      partnerCompanyIds.map((id) => ({ companyId: id })),
    );

  public bulkShiftDates = (
    projectId: number,
    payload: BulkShiftDatesRequest,
  ): Promise<void> =>
    this.post(`/projects/${projectId}/bulk-shift-dates`, payload);

  public paginateAndFilter = async ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
  }: PaginatedProjectParams): Promise<PaginatedProjectResponse> => {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    let url = '/projects/lightV1?';
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);

    if (filter.length) {
      url += `${filterAsString}&match=${filterType}&`;
    }
    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedProjectResponse>(url).then((response) => ({
      ...response,
      data: response.data.map(withProjectMetaAndPhaseMeta),
    }));
  };

  public getAllProjectCurrentPhases = (): Promise<ProjectPhase[]> =>
    this.get<ProjectPhase[]>('/projects/current-phases').then(
      (phases) => mapPhasesFieldAccessMetaInBase3(phases) ?? phases,
    );

  public getAllProjectCurrentPhasesV2 = ({
    search: searchValue,
    limit,
    offset,
  }: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: ProjectPhase[]; count: number }> =>
    this.get<{ data: ProjectPhase[]; count: number }>(
      '/projects/current-phases',
      {
        params: {
          searchValue,
          offset,
          limit,
        },
      },
    ).then((res) => ({
      ...res,
      data: mapPhasesFieldAccessMetaInBase3(res.data) ?? res.data,
    }));

  public gpt({ input }: GptSearchPayload): Promise<Project[]> {
    return this.post<Project[], GptSearchPayload>('/search', { input });
  }

  public getVariablesData = (projectId: number): Promise<any> =>
    this.get(`/variables/${projectId}`);

  public getProjectFinancialsData = (
    url: string,
  ): Promise<ProjectFinancialsApiData> => this.get(url);

  // Keeping two separate APIs below in order to handle Types
  public getProjectFinancials = async ({
    projectId,
    includeSubscriptionMeta,
    subscriptionPeriodId,
    queryParams,
    budgetId,
  }: {
    projectId: number;
    includeSubscriptionMeta?: boolean;
    subscriptionPeriodId?: string;
    queryParams?: ProjectFinancialsApiQueryParams;
    budgetId?: number;
  }): Promise<ProjectFinancialsApiData> =>
    this.getProjectFinancialsData(
      `/projects/${projectId}/financials${
        includeSubscriptionMeta
          ? '?includeSubscriptionMeta=true'
          : subscriptionPeriodId != null
          ? `?periodId=${subscriptionPeriodId}`
          : ''
      }${
        budgetId != null
          ? `${
              includeSubscriptionMeta || subscriptionPeriodId != null
                ? '&'
                : '?'
            }${stringify(
              { projectFinancialsBudgetId: budgetId },
              { skipNull: true },
            )}`
          : ''
      }${
        queryParams != null
          ? `&${stringify(
              {
                groupBy: queryParams.groupBy,
                zoomLevel: queryParams.zoomLevel,
                tabType: queryParams.tabType,
              },
              {
                skipNull: true,
              },
            )}`
          : ''
      }`,
    );

  public getProjectFinancialsWithQuery = async (
    projectId: number,
    queryParams: ProjectFinancialsApiQueryParams,
    budgetId?: number,
    subscriptionPeriodId?: string,
  ): Promise<ProjectFinancialsApiDataWithQuery> =>
    this.get(
      `/projects/${projectId}/financials?${
        budgetId != null
          ? `${stringify(
              { projectFinancialsBudgetId: budgetId },
              { skipNull: true },
            )}&`
          : ''
      }${stringify(
        {
          groupBy: queryParams.groupBy,
          zoomLevel: queryParams.zoomLevel,
          tabType: queryParams.tabType,
        },
        {
          skipNull: true,
        },
      )}${
        subscriptionPeriodId != null ? `&periodId=${subscriptionPeriodId}` : ''
      }`,
    );

  public getProjectPermissionsForCurrentUser = (
    projectId: number,
  ): Promise<UserPermissionForProject> =>
    this.get(`/projects/${projectId}/permissions`);

  /**
   *
   * @param projectId
   * @deprecated This has been deprecated in favour of V2 apis supporting multibudget financials
   * @returns
   */
  public getProjectBudgetSettings = async (
    projectId: number,
  ): Promise<ProjectFinancialsDetails> =>
    this.get(`projects/${projectId}/financials/settings`);

  public getProjectUserTeamsCostRates = async (
    projectId: number,
  ): Promise<CostRate[]> => this.get(`projects/${projectId}/cost-rates`);

  /**
   *
   * @param projectId
   * @param payload
   * @description Use updateProjectBudgetSettingsV2 to update any budget related details.
   * @returns
   */
  public updateProjectBudgetSettings = async (
    projectId: number,
    payload: ProjectFinancialsDetails,
  ): Promise<ProjectFinancialsDetails> =>
    this.put(`projects/${projectId}/financials/settings`, payload);

  public updateProjectGeneralSettingsV2 = async (
    projectId: number,
    payload: ProjectFinancialsDetails,
  ): Promise<ProjectFinancialsDetails> =>
    this.put(`projects/${projectId}/financialsV2/settings`, payload);

  public updateProjectCostRates = async (
    projectId: number,
    backDate: string,
    payload: Partial<CostRate>[],
  ): Promise<any> =>
    this.put(`projects/${projectId}/cost-rates?backdate=${backDate}`, payload);

  public getProjectRateCardVersion = (
    projectId: number,
    rateCardVersionId: number,
  ): Promise<RateCard> =>
    this.get(`projects/${projectId}/rate-cards/versions/${rateCardVersionId}`);

  public shiftTasks = async (
    projectId: number,
    payload: ShiftTasksPayload,
  ): Promise<ShiftTasksPayload> =>
    this.put(`projects/${projectId}/rearrange-phases-and-tasks`, {
      ...payload,
      useEntityOrder: true,
    });

  public getDependenciesMap = async (
    projectId: number,
  ): Promise<DependenciesMap | undefined> =>
    this.get(`projects/${projectId}/dependency-map`);

  public getPhasesForAProject = async (
    projectId: number,
  ): Promise<ProjectPhase[]> =>
    this.get(`projects/${projectId}/project-phases`);

  public getProjectStatusMapping = async (
    projectIds?: string,
  ): Promise<Dictionary<ProjectWithStatus>> =>
    this.get(`projects/status-mapping?projectIds=${projectIds}`);

  public getProjectFinancialContractMapping = async (): Promise<
    Dictionary<ProjectWithFinancialContractType>
  > => this.get('projects/financial-contract-mapping');

  public getProjectPartnerMapping = async (
    projectIds: number[],
  ): Promise<Dictionary<ProjectWithPartners>> => {
    return this.post('projects/partnersV2', projectIds);
  };

  public getProjectCreationStatus = async (
    projectId: number,
  ): Promise<ProjectCreationStatus[]> =>
    this.get(`projects/${projectId}/status`);

  public updateAllowedDomains = async (
    projectId: number,
    payload: UpdateAllowedDomainsForProject,
  ): Promise<CustomerPortalProjectMapping> => {
    return this.put(`/projects/${projectId}/allowed-domains`, payload);
  };

  public getShareUrlForProject = async (
    projectId: number,
    payload: GenerateShareUrlForProjectRequest,
  ): Promise<GenerateShareUrlResponse> => {
    return this.post(
      `projects/${projectId}/customer-portal-share-url`,
      payload,
    );
  };

  public addUsersFromShareUrl = (
    payload: AddUsersFromShareUrlRequest,
  ): Promise<AddUsersFromShareUrlResponse> => {
    return this.post('/projects/add-users-from-share-url', payload);
  };

  public getProjectAccessRequestedUsers = (
    projectId: number,
  ): Promise<ProjectAccessRequest[]> =>
    this.get(`projects/${projectId}/access-requested-users`);

  public approveProjectAccess = (
    projectId: number,
    payload: ApproveOrDenyProjectAccessPayload,
  ): Promise<ProjectAccessRequest> =>
    this.post(`projects/${projectId}/requested-users/approve-access`, payload);

  public rejectProjectAccess = (
    projectId: number,
    payload: ApproveOrDenyProjectAccessPayload,
  ): Promise<ProjectAccessRequest> =>
    this.post(`projects/${projectId}/requested-users/reject-access`, payload);

  public getCountOfTasksInProjectFromView = (
    projectId: number,
    viewId: number,
  ): Promise<{ count: number }> =>
    this.get<{ count: number }>(`projects/${projectId}/view/${viewId}/count`);

  public getfTasksInProjectFromView = (
    projectId: number,
    viewId: number,
  ): Promise<{ count: number; tasks: Task[] }> =>
    this.get<{ count: number; tasks: Task[] }>(
      `projects/${projectId}/view/${viewId}/tasks`,
    );

  /** V2 APIs for Multiline Budgets */
  public getProjectBudgetSettingsV2 = async (
    projectId: number,
  ): Promise<ProjectFinancialsDetails> =>
    this.get(`projects/${projectId}/financial-budgets/settings`);

  public createProjectBudgetSettingsV2 = async (
    projectId: number,
    payload: ProjectFinancialsBudgets,
  ): Promise<ProjectFinancialsBudgets> =>
    this.post(`/projects/${projectId}/financial-budgets/settings`, payload);

  public updateProjectBudgetSettingsV2 = async (
    projectId: number,
    projectFinancialBudgetId: number,
    payload: ProjectFinancialsBudgets,
  ): Promise<ProjectFinancialsBudgets> =>
    this.put(
      `/projects/${projectId}/financial-budgets/${projectFinancialBudgetId}/settings`,
      payload,
    );

  public deleteProjectBudget = async (
    payload: ProjectBudgetDeletePayload,
  ): Promise<void> => {
    const { projectId, projectFinancialsBudgetId, ...optionalFields } = payload;

    return this.delete(
      `/projects/${projectId}/financial-budgets/${projectFinancialsBudgetId}/settings`,
      { data: optionalFields },
    );
  };

  public markBudgetAsDefault = async (
    projectId: number,
    projectFinancialBudgetId: number,
  ): Promise<void> =>
    this.put(
      `/projects/${projectId}/financial-budgets/${projectFinancialBudgetId}/default`,
      undefined,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      { headers: { 'Content-Type': 'application/json' } },
    );

  public getMemberExclusionsForBudget = async (
    projectId: number,
    projectFinancialBudgetId: number,
  ): Promise<BudgetMemberExclusionItem[]> =>
    this.get(
      `/projects/${projectId}/financial-budgets/${projectFinancialBudgetId}/member-exclusions`,
    );

  public updateFinancialBudgetMembers = async (
    projectId: number,
    projectFinancialBudgetId: number,
    payload: { userId?: number; roleId?: number; teamId?: number }[],
  ): Promise<void> =>
    this.put(
      `/projects/${projectId}/financial-budgets/${projectFinancialBudgetId}/members`,
      payload,
    );

  public deleteBudgetMember = async (
    projectId: number,
    projectFinancialBudgetId: number,
    payload: { userId?: number; roleId?: number; teamId?: number }[],
  ): Promise<void> =>
    this.delete(
      `/projects/${projectId}/financial-budgets/${projectFinancialBudgetId}/members`,
      { data: payload },
    );

  public getFinancialBudgetAuditLogs = async (
    projectId: number,
    budgetId: number,
  ): Promise<AuditLog<ProjectFinancialsAuditLogMeta>[]> =>
    this.get(`/projects/${projectId}/financial-budgets/${budgetId}/audit`);

  public getFinancialBudgetsLite = async (
    projectIds: number[],
  ): Promise<Record<string, ProjectBudgetItemLite[]>> => {
    const projectIdsString = projectIds.join(',');

    return this.get(`/financial-budgets/lite?projectIds=${projectIdsString}`);
  };

  public getMemberExclusionsForProject = async (
    projectId: number,
  ): Promise<Dictionary<BudgetMemberExclusionItem[]>> =>
    this.get(`/projects/${projectId}/budget-member-exclusions`);

  public addMembersToBudget = async (payload: {
    budgetIds: number[];
    users: { userId: number }[];
    teams: { teamId: number }[];
  }): Promise<void> =>
    this.put('/financial-budgets/members/bulk-action', payload);

  public async getProjectFieldValues(
    projectIds: number[],
  ): Promise<FieldsWithUniqueValuesForBulkEditor[]> {
    return this.post('/projects/fields/unique-values', projectIds);
  }
}

export const ProjectAPI = new ProjectAPIClient();
export * from './types';
// Light tab metadata only — heavy assets (projectTabIconMap, PROJECT_TABS,
// createFontAwesomeIcon) live in ./tabs; deep-path them where needed so the
// shell barrel doesn't drag FontAwesome and the icon map into app.js.
export * from './tab-keys';
